import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FilePond } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import { FileSystemContext } from './FileSystem';
import ConfirmDialog from './ConfirmDialog';
import file from '../common/file';
import { FilePondFile } from 'filepond';
import apiUrl from '../assets/apiUrl';
import QueueTab from 'queuetab';
import AuthContext, { getAccessToken } from './Auth';
import folder from '../common/folder';

interface Props {
  activeFolder: null | string;
}

const FilePondWrapper: FC<Props> = ({ activeFolder }) => {
  const { useFiles, useFolders } = useContext(FileSystemContext);
  const { fetchWrapper } = useContext(AuthContext);
  const [files, setFiles] = useFiles();
  const [folders, setFolders] = useFolders();
  const FilePondRef = useRef<any>();
  const [overwriteTargets, setOverWriteTargets] = useState<file[]>([]);
  const [isOverWriteOpen, setIsOverWriteOpen] = useState(false);
  const [queuetab] = useState(new QueueTab());
  const checkOverwrite = (file: FilePondFile) => {
    const existingFiles = files.filter(
      (e) => e.folder_uid === (file.getMetadata('folder_uid') || activeFolder)
    );

    if (!existingFiles.map((e) => e.filename).includes(file.filename)) {
      return;
    }

    const targets = existingFiles.filter((e) => e.filename === file.filename);

    setOverWriteTargets((state) => state.concat(targets));
    setIsOverWriteOpen(true);
  };
  const beforeAddFile = (file: any): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      queuetab.add(
        async () => {
          const folder_uid = await recursiveFolderCheck(file.relativePath);
          if (folder_uid) {
            file.setMetadata('folder_uid', folder_uid ? folder_uid : 'null');
          } else {
            file.setMetadata('folder_uid', null);
          }
          return {};
        },
        () => {
          resolve(true);
          return {};
        }
      );
    });
  };
  const getUpToDateFolders = (): Promise<folder[]> => {
    return new Promise((resolve, reject) => {
      setFolders((fol: any) => {
        resolve(fol);
        return fol;
      });
    });
  };
  const createFolder = async (
    title: string,
    parentFolder?: string | null
  ): Promise<folder> => {
    return new Promise(async (resolve, reject) => {
      let queryString = `?title=${encodeURIComponent(title)}`;
      if (parentFolder) {
        queryString += `&parent_folder=${encodeURIComponent(parentFolder)}`;
      }

      const res = await (
        await fetchWrapper(`${apiUrl}/folder/create${queryString}`, {
          method: 'POST',
          headers: { 'x-access-token': await getAccessToken() },
        })
      ).json();
      resolve(res);
    });
  };
  const recursiveFolderCheck = async (path: string) => {
    return new Promise(async (resolve, reject) => {
      const parsedFolders = path.split('/').filter((e) => e);
      parsedFolders.pop();

      let i = 0;
      let iFolder = activeFolder;
      const newFolders: folder[] = [];

      while (i < parsedFolders.length) {
        const isFound = await (
          await getUpToDateFolders()
        ).find(
          (e) => e.parent_folder === iFolder && e.title === parsedFolders[i]
        );

        if (isFound) {
          iFolder = isFound.folder_uid;
        } else {
          const newFolder = await createFolder(parsedFolders[i], iFolder);
          iFolder = newFolder.folder_uid;
          newFolders.push(newFolder);
        }

        i++;
      }

      for (const fol of newFolders) {
        setFolders((state) => {
          if (state.find((e) => e.folder_uid === fol.folder_uid)) return state;
          return [...state, fol];
        });
      }

      resolve(iFolder);
    });
  };
  const [token, setToken] = useState<string>();
  useEffect(() => {
    const refreshToken = () => getAccessToken().then((res) => setToken(res));
    const handle = setInterval(refreshToken, 840000);
    refreshToken();
    return () => {
      clearInterval(handle);
    };
  }, []);
  const remFile = useCallback(
    async (file_uid: string) => {
      return fetchWrapper(`${apiUrl}/file/${file_uid}`, {
        method: 'DELETE',
        headers: {
          'x-access-token': await getAccessToken(),
        },
      });
    },
    [fetchWrapper]
  );
  const handleOverWrite = async (action: boolean) => {
    setIsOverWriteOpen(false);

    if (!action) {
      return setOverWriteTargets([]);
    }

    for (const target of overwriteTargets) {
      await remFile(target.file_uid);
    }

    setFiles(
      files.filter(
        (e) => !overwriteTargets.map((e) => e.file_uid).includes(e.file_uid)
      )
    );
    setOverWriteTargets([]);
  };
  return (
    <>
      <FilePond
        onprocessfile={(error, file) => {
          if (error) {
            console.log('there was an error');
          } else {
            setFiles((state: any) => {
              if (
                state.find(
                  (e: any) => e.file_uid === JSON.parse(file.serverId).file_uid
                )
              )
                return state;
              return [...state, JSON.parse(file.serverId)];
            });
            setTimeout(() => {
              FilePondRef.current.removeFile(file.id);
            }, 5000);
          }
        }}
        onprocessfilestart={checkOverwrite}
        allowMultiple={true}
        maxFiles={100}
        server={{
          url: `${apiUrl}/file?folder_uid=${activeFolder}`,
          headers: {
            'x-access-token': token || '',
          },
        }}
        name="uploads"
        labelIdle='Drag & Drop your files or <span class="filepond--label-action">Browse</span>'
        ref={FilePondRef}
        beforeAddFile={beforeAddFile}
      />
      <ConfirmDialog
        title={'Do you want to overwrite the following files?'}
        text={overwriteTargets.map((e) => e.filename).join(', ')}
        open={isOverWriteOpen}
        cancelButtonText={'Cancel'}
        confirmButtonText={'Confirm'}
        handleClose={handleOverWrite}
      />
    </>
  );
};

export default FilePondWrapper;
